import { Link, useLocation, useNavigate } from "react-router-dom";
import { useEffect, useState } from "react";
import { useAuth } from "../context/AuthContext";
import {
  House, Carrot, BookOpenText, SignOut, User as UserIcon, ChartLineUp, List as ListIcon, ShieldCheck, Buildings,
} from "@phosphor-icons/react";
import {
  Sheet, SheetContent, SheetTrigger,
} from "./ui/sheet";
import TrialBanner from "./TrialBanner";

const NAV = [
  { to: "/app", label: "Dashboard", icon: House, testid: "nav-dashboard" },
  { to: "/zutaten", label: "Zutaten", icon: Carrot, testid: "nav-ingredients" },
  { to: "/rezepte", label: "Rezepte", icon: BookOpenText, testid: "nav-recipes" },
];

const ADMIN_NAV = { to: "/admin", label: "Benutzer-Admin", icon: ShieldCheck, testid: "nav-admin" };
const SUPERADMIN_NAV = { to: "/firmen", label: "Firmen", icon: Buildings, testid: "nav-companies" };

function Clock() {
  const [now, setNow] = useState(new Date());
  useEffect(() => {
    const id = setInterval(() => setNow(new Date()), 1000);
    return () => clearInterval(id);
  }, []);
  return (
    <span className="font-mono-num text-[11px] sm:text-xs text-zinc-500 whitespace-nowrap" data-testid="topbar-clock">
      {now.toLocaleDateString("de-DE")} · {now.toLocaleTimeString("de-DE")}
    </span>
  );
}

function SidebarContent({ user, onNavigate, onLogout }) {
  const loc = useLocation();
  return (
    <div className="h-full flex flex-col bg-zinc-50">
      <div className="px-5 sm:px-6 py-5 border-b border-zinc-200 flex items-center gap-2">
        <div className="w-8 h-8 rounded-sm bg-[#002FA7] flex items-center justify-center">
          <ChartLineUp size={18} weight="bold" color="white" />
        </div>
        <div className="min-w-0">
          <div className="font-heading text-base font-bold leading-none">NutriCalc</div>
          <div className="text-[10px] uppercase tracking-widest text-zinc-500 mt-1 leading-tight break-words" data-testid="sidebar-company-name">
            {user?.company_name || "Lab Edition"}
          </div>
        </div>
      </div>

      <nav className="flex-1 px-3 py-4 space-y-1">
        {[
          ...NAV,
          ...(user?.is_admin ? [ADMIN_NAV] : []),
          ...(user?.is_superadmin ? [SUPERADMIN_NAV] : []),
        ].map(({ to, label, icon: Icon, testid }) => {
          const active = loc.pathname === to || (to !== "/app" && loc.pathname.startsWith(to));
          return (
            <Link
              key={to}
              to={to}
              onClick={onNavigate}
              data-testid={testid}
              className={`flex items-center gap-3 px-3 py-2.5 text-sm rounded-sm transition-colors ${
                active
                  ? "bg-[#002FA7] text-white"
                  : "text-zinc-700 hover:bg-zinc-200/60"
              }`}
            >
              <Icon size={16} weight={active ? "fill" : "regular"} />
              {label}
            </Link>
          );
        })}
      </nav>

      <div className="px-3 pb-3 text-[10px] text-zinc-400 leading-relaxed">
        <div className="flex flex-wrap gap-x-2">
          <Link to="/datenschutz" className="hover:text-zinc-600" onClick={onNavigate} data-testid="legal-privacy">Datenschutz</Link>
          <span>·</span>
          <Link to="/impressum" className="hover:text-zinc-600" onClick={onNavigate} data-testid="legal-imprint">Impressum</Link>
          <span>·</span>
          <Link to="/agb" className="hover:text-zinc-600" onClick={onNavigate} data-testid="legal-terms">AGB</Link>
        </div>
      </div>

      <div className="border-t border-zinc-200 p-3">
        <div className="flex items-center gap-2 px-2 py-2 text-sm" data-testid="sidebar-user">
          <div className="w-8 h-8 rounded-full bg-zinc-200 flex items-center justify-center">
            <UserIcon size={16} />
          </div>
          <div className="flex-1 min-w-0">
            <div className="truncate font-medium leading-tight">{user?.full_name}</div>
            <div className="truncate text-[11px] text-zinc-500">@{user?.username}</div>
          </div>
        </div>
        <button
          onClick={onLogout}
          data-testid="logout-button"
          className="mt-2 w-full flex items-center justify-center gap-2 px-3 py-2 text-xs rounded-sm border border-zinc-200 bg-white hover:bg-zinc-100 transition-colors"
        >
          <SignOut size={14} /> Abmelden
        </button>
      </div>
    </div>
  );
}

export default function Layout({ children }) {
  const { user, logout } = useAuth();
  const nav = useNavigate();
  const [open, setOpen] = useState(false);

  const handleLogout = async () => { await logout(); nav("/login"); };

  return (
    <div className="min-h-screen flex bg-white">
      {/* Desktop sidebar */}
      <aside className="hidden md:flex w-60 lg:w-64 shrink-0 border-r border-zinc-200 flex-col" data-testid="sidebar">
        <SidebarContent user={user} onLogout={handleLogout} />
      </aside>

      <div className="flex-1 flex flex-col min-w-0">
        <header className="h-14 px-4 sm:px-6 md:px-8 border-b border-zinc-200 flex items-center justify-between bg-white sticky top-0 z-30">
          <div className="flex items-center gap-2 min-w-0">
            {/* Mobile menu trigger */}
            <Sheet open={open} onOpenChange={setOpen}>
              <SheetTrigger asChild>
                <button
                  className="md:hidden p-2 -ml-2 rounded-sm hover:bg-zinc-100"
                  aria-label="Menü öffnen"
                  data-testid="mobile-menu-button"
                >
                  <ListIcon size={20} weight="bold" />
                </button>
              </SheetTrigger>
              <SheetContent side="left" className="p-0 w-72 max-w-[85vw]" data-testid="mobile-sidebar">
                <SidebarContent user={user} onLogout={handleLogout} onNavigate={() => setOpen(false)} />
              </SheetContent>
            </Sheet>
            <div className="md:hidden flex items-center gap-1.5 min-w-0">
              <div className="w-7 h-7 rounded-sm bg-[#002FA7] flex items-center justify-center shrink-0">
                <ChartLineUp size={14} weight="bold" color="white" />
              </div>
              <span className="font-heading font-bold text-sm truncate">NutriCalc</span>
            </div>
            <div className="hidden md:block text-[11px] uppercase tracking-widest text-zinc-500 truncate">
              Nährwertberechnung · EU VO 1169/2011
            </div>
          </div>
          <Clock />
        </header>
        <main className="flex-1 bg-white">
          <div className="px-4 sm:px-6 md:px-8 py-5 sm:py-7 md:py-8">
            <TrialBanner />
            {children}
          </div>
        </main>
      </div>
    </div>
  );
}
